/**
 * Asking for an arrangement in words: "a wind quintet, fill in the harmony, arpeggios".
 *
 * The words go to the server's language model (`POST /theory/ask`), in any language,
 * with the roster and settings as they stand and the score's length and parts, and come back as
 * the roster and settings they ask for. The toolbar above changes, the board arranges it at once
 * (`lib/ask-run`: the first bars of a long window first), and this strip says exactly what
 * changed (`lib/ask-summary`) and what is being arranged. What the words asked for that no
 * setting can do is said beside it, so a request the arranger cannot meet is never silently
 * dropped. Where the words could be read two ways that arrange differently, the answer is a
 * question with its answers as buttons instead, in the words' language: pressing one asks again
 * with it, and an answered request is never asked again.
 */

import { useRef, useState } from "react";
import {
  ASK_LIMIT,
  type AskAnswered,
  type AskPayload,
  type AskQuestion,
  type AskScore,
  type AskSettings,
  type Seat,
} from "@app/shared-types";
import { api } from "../api/client";
import { askSummary } from "../lib/ask-summary";
import { ACTION_LIVE, LABEL } from "./Chrome";

export interface ArrangeAskProps {
  readonly roster: readonly string[];
  readonly settings: AskSettings;
  /** The score open, or `null` before one is. */
  readonly score: AskScore | null;
  readonly seats: readonly Seat[];
  /** Set the board as `answer` asks and arrange it; what is being arranged, in words. */
  onAnswer(answer: AskPayload): string | null;
}

/** What the last request came to: the changes made and what is arranged, or why none were. */
interface Said {
  readonly changed: readonly string[];
  readonly unmet: string | null;
  readonly arranging: string | null;
  readonly failed: string | null;
}

export default function ArrangeAsk({
  roster,
  settings,
  score,
  seats,
  onAnswer,
}: ArrangeAskProps): JSX.Element {
  const [words, setWords] = useState("");
  const [asking, setAsking] = useState(false);
  const [said, setSaid] = useState<Said | null>(null);
  // The question the last words were answered with, and those words, asked again with a choice.
  const [question, setQuestion] = useState<{ words: string; asked: AskQuestion } | null>(null);
  // The toolbar stays live while the model reads: the answer is said against the settings as
  // they are when it arrives, so a change it undoes that was made meanwhile is said too, and is
  // handed to the board as it is then, with the run then on screen.
  const now = useRef({ roster, settings, onAnswer });
  now.current = { roster, settings, onAnswer };

  const ask = async (asked: string, answered: AskAnswered | null): Promise<void> => {
    if (asking || asked.trim() === "") return;
    setAsking(true);
    setSaid(null);
    setQuestion(null);
    try {
      const answer = await api.ask({
        words: asked,
        roster: [...roster],
        settings,
        score,
        answered,
      });
      if (answer.question !== null) {
        setQuestion({ words: asked, asked: answer.question });
        return;
      }
      const before = now.current;
      const arranging = before.onAnswer(answer);
      const changed = askSummary(before, answer, seats);
      setSaid({ changed, unmet: answer.unmet, arranging, failed: null });
    } catch (failed) {
      setSaid({
        changed: [],
        unmet: null,
        arranging: null,
        failed: failed instanceof Error ? failed.message : String(failed),
      });
    } finally {
      setAsking(false);
    }
  };

  return (
    <form
      className="flex shrink-0 flex-wrap items-center gap-x-2.5 gap-y-1.5 border-b border-shell-edge bg-shell-bar px-2.5 py-1.5"
      onSubmit={(event) => {
        event.preventDefault();
        void ask(words, null);
      }}
    >
      <span className={LABEL}>Ask</span>
      <input
        value={words}
        onChange={(event) => setWords(event.target.value)}
        maxLength={ASK_LIMIT}
        placeholder="In words, in any language: a wind quintet, fill in the harmony, arpeggios"
        title="Say who should play and how, in words. A language model sets the toolbar to match and arranges it, the first 16 bars first where there are more."
        className="h-7 min-w-0 flex-1 basis-64 rounded bg-shell-key px-2 text-[11px] text-shell-lit outline-none placeholder:text-shell-dim focus:ring-1 focus:ring-shell-live"
      />
      <button type="submit" className={ACTION_LIVE} disabled={asking || words.trim() === ""}>
        {asking ? "Reading…" : "Ask"}
      </button>
      {question === null ? null : (
        <p className="flex w-full flex-wrap items-center gap-2 text-[11px] text-shell-lit">
          {question.asked.text}
          {question.asked.choices.map((choice) => (
            <button
              key={choice.words}
              type="button"
              disabled={asking}
              onClick={() =>
                void ask(question.words, { question: question.asked.text, choice: choice.words })
              }
              className="h-6 rounded border border-shell-seam px-2.5 text-shell-text hover:bg-shell-key"
            >
              {choice.label}
            </button>
          ))}
        </p>
      )}
      {said === null ? null : <SaidLine said={said} />}
    </form>
  );
}

function SaidLine({ said }: { said: Said }): JSX.Element {
  if (said.failed !== null) {
    return (
      <p role="alert" className="w-full rounded bg-fault/15 px-2 py-1 text-[11px] text-shell-lit">
        {said.failed}
      </p>
    );
  }
  const changed =
    said.changed.length > 0
      ? `${said.changed.join(" · ")}.`
      : said.arranging === null
        ? "Nothing changed."
        : "";
  return (
    <p className="w-full text-[11px] text-shell-text">
      {changed}
      {said.unmet === null ? null : (
        // The model's sentence, closed where it left it open, so the next one does not run on.
        <span className="text-shell-lit">
          {" "}
          Not done: {/[.!?]$/.test(said.unmet) ? said.unmet : `${said.unmet}.`}
        </span>
      )}
      {said.arranging === null ? null : <span className="text-shell-dim"> {said.arranging}</span>}
    </p>
  );
}
